import { useEffect, useRef, useState, type ReactNode } from 'react';
import { appCopy } from '@zibanu/shared';
import { cx } from './cx';
import { Button, Icon, IconButton } from './basics';
import type { IconName } from './icons';

export interface Org {
  id: string;
  name: string;
  /** In words: appCopy.role(…). */
  role?: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export interface OrgSwitcherProps {
  orgs: Org[];
  value?: string;
  onChange?: (id: string) => void;
  /** Books are started in chat: the app opens WhatsApp. */
  onAddBusiness?: () => void;
  compact?: boolean;
  className?: string;
}

/** Which books you're looking at, always top left (design system). Escape or a tap outside closes it. */
export function OrgSwitcher({ orgs, value, onChange, onAddBusiness, compact, className }: OrgSwitcherProps) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const current = orgs.find((o) => o.id === value) ?? orgs[0] ?? { id: '', name: appCopy.wordmark() };
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        opener.current?.focus();
        return;
      }
      const items = [...(root.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])];
      if (items.length === 0) return;
      const at = items.indexOf(document.activeElement as HTMLElement);
      const go = (i: number) => {
        e.preventDefault();
        items[(i + items.length) % items.length]!.focus();
      };
      if (e.key === 'ArrowDown') go(at + 1);
      else if (e.key === 'ArrowUp') go(at < 0 ? items.length - 1 : at - 1);
      else if (e.key === 'Home') go(0);
      else if (e.key === 'End') go(items.length - 1);
    };
    const items = root.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]');
    const selected = root.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    (selected ?? items?.[0])?.focus();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);
  return (
    <div ref={root} className={cx('zb-org', compact && 'zb-org--compact', className)}>
      <button ref={opener} type="button" className="zb-org__btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="zb-org__tile" aria-hidden="true">
          {initials(current.name)}
        </span>
        <span className="zb-org__text">
          <span className="zb-org__name">{current.name}</span>
          {!compact && current.role && <span className="zb-org__role">{current.role}</span>}
        </span>
        <Icon name="chevron-down" size={20} />
      </button>
      {open && (
        <div role="menu" aria-label={appCopy.switchBusiness()} className="zb-menu">
          <div className="zb-menu__label">{appCopy.switchBusiness()}</div>
          {orgs.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitemradio"
              aria-checked={o.id === current.id}
              tabIndex={o.id === current.id ? 0 : -1}
              className={cx('zb-menu__item', o.id === current.id && 'is-on')}
              onClick={() => {
                setOpen(false);
                opener.current?.focus();
                onChange?.(o.id);
              }}
            >
              <span className="zb-org__tile" aria-hidden="true">
                {initials(o.name)}
              </span>
              <span className="zb-org__text">
                <span className="zb-org__name">{o.name}</span>
                {o.role && <span className="zb-org__role">{o.role}</span>}
              </span>
              {o.id === current.id && <Icon name="check" size={20} />}
            </button>
          ))}
          {onAddBusiness && (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="zb-menu__item zb-menu__item--add"
              onClick={() => {
                setOpen(false);
                opener.current?.focus();
                onAddBusiness();
              }}
            >
              <Icon name="plus" size={20} />
              {appCopy.addBusiness()}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export interface AppHeaderProps {
  orgs?: Org[];
  org?: string;
  onOrgChange?: (id: string) => void;
  onAddBusiness?: () => void;
  title?: string;
  actions?: ReactNode;
  variant?: 'mobile' | 'desktop';
  /** A pushed screen: Back and the title instead of the books switcher. */
  onBack?: () => void;
  className?: string;
}

export function AppHeader({ orgs, org, onOrgChange, onAddBusiness, title, actions, variant = 'mobile', onBack, className }: AppHeaderProps) {
  if (onBack) {
    return (
      <header className={cx('zb-header', `zb-header--${variant}`, 'zb-header--detail', className)}>
        <IconButton icon="chevron-left" label={appCopy.back()} onClick={onBack} className="zb-header__back" />
        <div className="zb-header__start">
          <h1 className="zb-header__title zb-header__title--detail">{title}</h1>
        </div>
        <div className="zb-header__end">{actions}</div>
      </header>
    );
  }
  return (
    <header className={cx('zb-header', `zb-header--${variant}`, className)}>
      <div className="zb-header__start">
        {orgs ? (
          <OrgSwitcher
            orgs={orgs}
            {...(org ? { value: org } : {})}
            {...(onOrgChange ? { onChange: onOrgChange } : {})}
            {...(onAddBusiness ? { onAddBusiness } : {})}
            compact={variant === 'mobile'}
          />
        ) : (
          <h1 className="zb-header__title">{title}</h1>
        )}
      </div>
      {variant === 'desktop' && title && orgs && <h1 className="zb-header__title zb-header__title--mid">{title}</h1>}
      <div className="zb-header__end">{actions}</div>
    </header>
  );
}

export type TabId = 'home' | 'records' | 'capture' | 'customers' | 'more';

const TABS: { id: TabId; label: () => string; icon: IconName }[] = [
  { id: 'home', label: appCopy.tabHome, icon: 'house' },
  { id: 'records', label: appCopy.tabRecords, icon: 'receipt-text' },
  { id: 'capture', label: appCopy.tabCapture, icon: 'plus' },
  { id: 'customers', label: appCopy.tabCustomers, icon: 'users-round' },
  { id: 'more', label: appCopy.tabMore, icon: 'ellipsis' },
];

export interface BottomTabBarProps {
  active?: TabId;
  onChange?: (id: TabId) => void;
  /** A count on a tab; on Capture, the captures waiting to send. */
  badges?: Partial<Record<TabId, number>>;
  /** False when the person can't record: the disc goes (spec 3A §6.2). */
  showCapture?: boolean;
  className?: string;
}

/** Home · Records · ⊕ Capture · Customers · More (parent spec D9). */
export function BottomTabBar({ active = 'home', onChange, badges = {}, showCapture = true, className }: BottomTabBarProps) {
  return (
    <nav className={cx('zb-tabbar', className)} aria-label="Main">
      {TABS.filter((tab) => showCapture || tab.id !== 'capture').map((tab) => {
        const count = badges[tab.id] ?? 0;
        if (tab.id === 'capture') {
          return (
            <button
              key={tab.id}
              type="button"
              className="zb-tab zb-tab--capture"
              onClick={() => onChange?.(tab.id)}
              aria-label={count > 0 ? `Capture a record, ${appCopy.waitingToSend(count)}` : 'Capture a record'}
            >
              <span className="zb-tab__disc">
                <Icon name="plus" size={28} strokeWidth={2.5} />
                {count > 0 && (
                  <span className="zb-tab__dot" aria-hidden="true">
                    {count}
                  </span>
                )}
              </span>
              <span className="zb-tab__label">{tab.label()}</span>
            </button>
          );
        }
        const on = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            className={cx('zb-tab', on && 'is-on')}
            aria-current={on ? 'page' : undefined}
            onClick={() => onChange?.(tab.id)}
          >
            <span className="zb-tab__pill">
              <Icon name={tab.icon} size={24} strokeWidth={on ? 2.5 : 2} />
              {count > 0 && <span className="zb-tab__dot">{count}</span>}
            </span>
            <span className="zb-tab__label">{tab.label()}</span>
          </button>
        );
      })}
    </nav>
  );
}

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  count?: number;
}

export interface SideNavProps {
  active?: string;
  items: NavItem[];
  onChange?: (id: string) => void;
  onCapture?: () => void;
  captureCount?: number;
  className?: string;
}

/** The desktop sidebar (1024 px and up): dark kola in every theme, Capture at the top. */
export function SideNav({ active, items, onChange, onCapture, captureCount = 0, className }: SideNavProps) {
  return (
    <nav className={cx('zb-side', className)} aria-label="Main">
      <div className="zb-side__brand">{appCopy.wordmark()}</div>
      {onCapture && (
        <div className="zb-side__cta">
          <Button icon="plus" block onClick={onCapture} aria-label={captureCount > 0 ? `${appCopy.tabCapture()}, ${appCopy.waitingToSend(captureCount)}` : appCopy.tabCapture()}>
            {appCopy.tabCapture()}
          </Button>
        </div>
      )}
      <ul className="zb-side__list">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={cx('zb-side__item', active === item.id && 'is-on')}
              aria-current={active === item.id ? 'page' : undefined}
              onClick={() => onChange?.(item.id)}
            >
              <Icon name={item.icon} size={24} />
              {item.label}
              {item.count ? <span className="zb-side__count">{item.count}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
